import { Loader } from "@/components/loader/Loader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import {
  REFERRED_ACCOUNTS_PAGE_SIZE,
  referralLinkQueryOptions,
  referredAccountsQueryOptions,
} from "@/shared/referralQueries.ts";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { panelDescriptionClass, SettingsPanelHeader } from "./SettingsPanelHeader.tsx";

async function copyLink(url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    confirmationToast("Link copied");
  } catch {
    errorToast("Couldn't copy the link");
  }
}

const ReferralLink = () => {
  const { data, isPending, isError } = useQuery(referralLinkQueryOptions);

  if (isPending) {
    return (
      <div className="py-6 flex justify-center">
        <Loader size="sm" />
      </div>
    );
  }
  if (isError) {
    return <p className="text-destructive text-sm">Couldn't load your referral link.</p>;
  }
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs">{data.url}</code>
      <Button
        size="sm"
        data-statsig-name="referral-link-copy"
        onClick={() => void copyLink(data.url)}
      >
        Copy
      </Button>
    </div>
  );
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * The accounts that signed up through the user's link: name, email or phone,
 * and date, newest first, a page at a time, under a total count.
 */
const ReferredAccounts = () => {
  const [page, setPage] = useState(0);
  const { data, isPending, isError } = useQuery({
    ...referredAccountsQueryOptions(page),
    // Keeps the current page on screen while the next one loads.
    placeholderData: keepPreviousData,
  });

  // A page emptied by deletions since it was counted falls back to the first.
  useEffect(() => {
    if (data && data.accounts.length === 0 && page > 0) setPage(0);
  }, [data, page]);

  if (isPending) return null;
  if (isError) {
    return <p className="text-destructive text-sm">Couldn't load who signed up.</p>;
  }
  if (data.total === 0) {
    return (
      <p className="text-sm text-muted-foreground">No one has signed up with your link yet.</p>
    );
  }
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-2">
        {data.total === 1 ? "1 person" : `${data.total} people`} signed up with your link.
      </p>
      <ul className="divide-y rounded-2xl border bg-card">
        {data.accounts.map((account) => (
          <li key={account.accountId} className="flex items-center gap-3 px-3 py-2">
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{account.name ?? account.displayName}</p>
              {account.name && (
                <p className="truncate text-xs text-muted-foreground">{account.displayName}</p>
              )}
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatDate(account.signedUpAt)}
            </span>
          </li>
        ))}
      </ul>
      <Pager page={page} total={data.total} onPage={setPage} />
    </div>
  );
};

/** "Page X of Y" with previous/next buttons for the referred-accounts list. */
const Pager = ({
  page,
  total,
  onPage,
}: {
  page: number;
  total: number;
  onPage: (page: number) => void;
}) => {
  const pageCount = Math.ceil(total / REFERRED_ACCOUNTS_PAGE_SIZE);
  return (
    <div className="mt-2 flex items-center justify-end gap-2">
      <span className="text-xs text-muted-foreground">
        Page {page + 1} of {pageCount}
      </span>
      <Button
        variant="outline"
        size="icon"
        aria-label="Previous page"
        disabled={page === 0}
        onClick={() => onPage(page - 1)}
        data-statsig-name="referred-accounts-prev-page"
      >
        <ChevronLeft className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        aria-label="Next page"
        disabled={page >= pageCount - 1}
        onClick={() => onPage(page + 1)}
        data-statsig-name="referred-accounts-next-page"
      >
        <ChevronRight className="size-4" />
      </Button>
    </div>
  );
};

export const ReferralsPanel = () => (
  <div className="w-full pb-12 space-y-6">
    <div>
      <SettingsPanelHeader title="Referrals" />
      <p className={panelDescriptionClass}>
        Share your link. Anyone who signs up for Okthx through it is credited to you.
      </p>
      <ReferralLink />
    </div>
    <ReferredAccounts />
  </div>
);
